import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useRest, RCard, RStat, RHead, RestLoading, RestPageBrief, SourceBadge, money, money2, RestReconnect } from './restui';
import { MonthlyChart } from './RestMissionControl';

export const CHANNELS = {
  pos: { es: 'Walk-in / local', en: 'Walk-in / dine-in', color: 'var(--rest-accent)', dot: '#F77F11' },
  online: { es: 'Online directo', en: 'Direct online', color: '#0EA5E9', dot: '#0EA5E9' },
  delivery: { es: 'Delivery 3rd party', en: '3rd-party delivery', color: '#8B5CF6', dot: '#8B5CF6' },
};

const fmtPeriod = (s, granularity, isEs, group) => {
  const loc = isEs ? 'es-ES' : 'en-US';
  const g = group || granularity;
  if (g === 'month') return new Date(`${s.period}-15T12:00:00`).toLocaleDateString(loc, { month: 'short', year: '2-digit' });
  if (g === 'year') return s.period;
  if (g === 'week') {
    const a = new Date(`${s.from}T12:00:00`); const b = new Date(`${s.to}T12:00:00`);
    return `${a.toLocaleDateString(loc, { day: 'numeric', month: 'short' })} – ${b.toLocaleDateString(loc, { day: 'numeric', month: 'short' })}`;
  }
  return new Date(`${s.period}T12:00:00`).toLocaleDateString(loc, { weekday: 'short', day: 'numeric', month: 'short' });
};

function BarTooltip({ s, granularity, isEs, view, edge }) {
  const pos = edge === 'left' ? 'left-0' : edge === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2';
  return (
    <div className={`pointer-events-none absolute bottom-full mb-2 ${pos} hidden group-hover:block z-30 rounded-xl bg-gray-900 text-white px-3 py-2.5 text-[11px] leading-relaxed whitespace-nowrap shadow-xl`}>
      <p className="font-bold text-[11.5px] capitalize">{fmtPeriod(s, granularity, isEs)}</p>
      <p className="mt-0.5">{money(s.total)} · {s.orders.toLocaleString()} {isEs ? 'órdenes' : 'orders'}</p>
      {view === 'mix' ? (
        <div className="mt-1 space-y-0.5">
          {['pos', 'online', 'delivery'].map((k) => (
            <p key={k} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: CHANNELS[k].dot }} />
              {isEs ? CHANNELS[k].es : CHANNELS[k].en}: <b className="tabular-nums">{money(s[k])}</b>
            </p>
          ))}
        </div>
      ) : s.without != null && (
        <div className="mt-1 space-y-0.5">
          <p className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-gray-400" />{isEs ? 'Sin Kitxens (est.)' : 'Without Kitxens (est.)'}: <b className="tabular-nums">{money(s.without)}</b></p>
          <p className="flex items-center gap-1.5 text-[#FDBA74]"><span className="w-2 h-2 rounded-sm bg-[#F77F11]" />{isEs ? 'Aporte Kitxens' : 'Kitxens added'}: <b className="tabular-nums">+{money(s.kx_add)}</b>{s.without > 0 && <span className="text-white/60">(+{Math.round(100 * s.kx_add / s.without)}%)</span>}</p>
        </div>
      )}
    </div>
  );
}

function RevenueBars({ series, granularity, isEs, view }) {
  const max = Math.max(1, ...series.map((s) => s.total));
  const monthly = granularity === 'month';
  return (
    <div>
      <div className={`flex items-end h-44 ${monthly ? 'gap-2 sm:gap-3' : 'gap-[3px]'}`}>
        {series.map((s, i) => {
          const h = Math.max(3, 100 * s.total / max);
          const edge = i < 2 ? 'left' : i > series.length - 3 ? 'right' : 'mid';
          return (
            <div key={s.period} className="group relative flex-1 flex flex-col justify-end h-full min-w-0" data-testid={`rev-bar-${s.period}`}>
              <BarTooltip s={s} granularity={granularity} isEs={isEs} view={view} edge={edge} />
              {view === 'mix' ? (
                <div className={`w-full ${monthly ? 'max-w-[64px] mx-auto' : ''} flex flex-col justify-end rounded-t overflow-hidden kx-grow-y opacity-85 group-hover:opacity-100 transition-opacity`}
                  style={{ height: `${h}%`, animationDelay: `${Math.min(i * 6, 400)}ms` }}>
                  {['delivery', 'online', 'pos'].map((k) => (
                    <div key={k} style={{ height: `${s.total ? 100 * s[k] / s.total : 0}%`, background: CHANNELS[k].color }} />
                  ))}
                </div>
              ) : (
                <div className={`w-full ${monthly ? 'max-w-[64px] mx-auto' : ''} flex flex-col justify-end rounded-t overflow-hidden kx-grow-y opacity-85 group-hover:opacity-100 transition-opacity`}
                  style={{ height: `${h}%`, animationDelay: `${Math.min(i * 6, 400)}ms` }}>
                  {s.without != null && s.total > 0 && (
                    <div className="bg-[var(--rest-accent)]" style={{ height: `${100 * s.kx_add / s.total}%` }} />
                  )}
                  <div className={s.without != null ? 'bg-gray-300 flex-1' : 'bg-[var(--rest-accent)] flex-1'} />
                </div>
              )}
              {monthly && (
                <span className="mt-1.5 block text-center text-[9.5px] sm:text-[10.5px] text-gray-400 capitalize truncate">{fmtPeriod(s, granularity, isEs)}</span>
              )}
            </div>
          );
        })}
      </div>
      {!monthly && (
        <div className="flex justify-between mt-2 text-[10px] text-gray-400">
          <span>{series[0]?.period}</span><span>{series[series.length - 1]?.period}</span>
        </div>
      )}
    </div>
  );
}

function BreakdownSection({ isEs, hasKx }) {
  const [group, setGroup] = useState('month');
  const [page, setPage] = useState(1);
  const { data: d, loading } = useRest(`/sales/breakdown?group=${group}`);
  useEffect(() => { setPage(1); }, [group]);
  const rows = d?.rows || [];
  const PER = 10;
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const cur = Math.min(page, pages);
  const pageRows = rows.slice((cur - 1) * PER, cur * PER);
  const GROUPS = [['day', isEs ? 'Día' : 'Day'], ['week', isEs ? 'Semana' : 'Week'], ['month', isEs ? 'Mes' : 'Month'], ['year', isEs ? 'Año' : 'Year']];
  return (
    <RCard className="mt-5 overflow-hidden" testid="rest-rev-breakdown">
      <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 pt-5 pb-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{isEs ? 'Comparativa · análisis por periodo' : 'Comparison · analysis by period'}</p>
          {hasKx && <p className="mt-0.5 text-[11px] text-gray-400 inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-[var(--rest-accent)]" />{isEs ? 'Incluye estimado con y sin Kitxens' : 'Includes with/without Kitxens estimate'}</p>}
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-full bg-gray-100 p-1" data-testid="rev-breakdown-groups">
          {GROUPS.map(([g, l]) => (
            <button key={g} onClick={() => setGroup(g)} data-testid={`rev-group-${g}`}
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors ${group === g ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="rev-breakdown-table">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-gray-400 bg-gray-50">
              <th className="text-left px-4 sm:px-6 py-2.5 font-bold">{isEs ? 'Periodo' : 'Period'}</th>
              <th className="text-right px-3 py-2.5 font-bold">{isEs ? 'Ventas' : 'Sales'}</th>
              <th className="text-right px-3 py-2.5 font-bold">{isEs ? 'Órdenes' : 'Orders'}</th>
              <th className="text-right px-3 py-2.5 font-bold"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: CHANNELS.pos.dot }} />{isEs ? 'Walk-in' : 'Walk-in'}</span></th>
              <th className="text-right px-3 py-2.5 font-bold"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: CHANNELS.online.dot }} />{isEs ? 'Online directo' : 'Direct online'}</span></th>
              <th className="text-right px-3 py-2.5 font-bold"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: CHANNELS.delivery.dot }} />Delivery 3rd</span></th>
              {hasKx && <th className="text-right px-3 py-2.5 font-bold text-gray-500">{isEs ? 'Sin Kitxens (est.)' : 'Without Kitxens (est.)'}</th>}
              {hasKx && <th className="text-right px-4 sm:px-6 py-2.5 font-bold text-[var(--rest-accent)]">{isEs ? 'Aporte Kitxens' : 'Kitxens added'}</th>}
            </tr>
          </thead>
          <tbody>
            {loading && !d && <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-400">…</td></tr>}
            {pageRows.map((r) => (
              <tr key={r.period} className="border-t border-gray-50 hover:bg-orange-50/40 transition-colors" data-testid={`rev-breakdown-row-${r.period}`}>
                <td className="px-4 sm:px-6 py-2.5 font-semibold text-gray-900 capitalize whitespace-nowrap">{fmtPeriod(r, null, isEs, group)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold text-gray-900">{money(r.total)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{r.orders.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{money(r.pos)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{money(r.online)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{money(r.delivery)}</td>
                {hasKx && <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">{r.without != null ? money(r.without) : '—'}</td>}
                {hasKx && <td className="px-4 sm:px-6 py-2.5 text-right tabular-nums font-bold text-[var(--rest-accent)]">{r.kx_add ? `+${money(r.kx_add)}` : '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-50" data-testid="rev-breakdown-pager">
          <button onClick={() => setPage(cur - 1)} disabled={cur <= 1} data-testid="rev-breakdown-prev"
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-[12px] text-gray-500 tabular-nums">{isEs ? 'pág.' : 'page'} {cur} / {pages}</span>
          <button onClick={() => setPage(cur + 1)} disabled={cur >= pages} data-testid="rev-breakdown-next"
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </RCard>
  );
}

export default function RestRevenue() {
  const { isEs } = useOutletContext();
  const [days, setDays] = useState(30);
  const [view, setView] = useState('kx');
  const { data: d, err, loading } = useRest(`/sales?days=${days}`);
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;
  if (!d.has_data) {
    return (
      <div data-testid="rest-revenue">
        <RHead title="Revenue" />
        <RCard className="p-10 text-center"><p className="text-gray-500 text-[14px]">{isEs ? 'Sin datos aún — importa un reporte en Reports.' : 'No data yet — import a report in Reports.'}</p></RCard>
      </div>
    );
  }
  const hasKx = !!d.kitxens_driven;
  const chartView = hasKx ? view : 'mix';
  return (
    <div data-testid="rest-revenue">
      <RHead title="Revenue" sub={`${d.window.from} → ${d.window.to}`}
        right={
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rest-revenue-range"
              className="rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-600">
              <option value={7}>{isEs ? '7 días' : '7 days'}</option>
              <option value={30}>{isEs ? '30 días' : '30 days'}</option>
              <option value={90}>{isEs ? '90 días' : '90 days'}</option>
              <option value={180}>{isEs ? '6 meses' : '6 months'}</option>
              <option value={365}>{isEs ? '12 meses' : '12 months'}</option>
            </select>
            <SourceBadge source="manual_imports" isEs={isEs} />
          </div>
        } />
      {(() => {
        const half = Math.floor(d.series.length / 2) || 1;
        const avg = (arr) => arr.reduce((a, s) => a + s.total, 0) / (arr.length || 1);
        const recent = avg(d.series.slice(half));
        const before = avg(d.series.slice(0, half));
        const falling = before > 0 && recent < before * 0.9;
        return (
          <RestPageBrief isEs={isEs} testid="rest-rev-brief"
            summary={isEs
              ? `${days} días: ${money(d.totals.total)} en ${d.totals.orders.toLocaleString()} órdenes (ticket ${money2(d.totals.avg_ticket)}).${d.kitxens_driven ? ` Kitxens generó ${money(d.kitxens_driven.sales)} — el ${d.kitxens_driven.pct}%.` : ''}`
              : `${days} days: ${money(d.totals.total)} across ${d.totals.orders.toLocaleString()} orders (avg ticket ${money2(d.totals.avg_ticket)}).${d.kitxens_driven ? ` Kitxens drove ${money(d.kitxens_driven.sales)} — ${d.kitxens_driven.pct}%.` : ''}`}
            alert={falling ? (isEs ? 'La tendencia reciente va a la baja frente al inicio del periodo — revisa el detalle por día.' : 'Recent trend is down vs the start of the period — review the daily breakdown.') : null}
            kpi={{ label: isEs ? 'Ventas' : 'Sales', value: money(d.totals.total), sub: `${d.totals.orders.toLocaleString()} ${isEs ? 'órdenes' : 'orders'}` }} />
        );
      })()}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {(() => {
          const c = d.compare;
          const cmpTxt = days === 7 ? (isEs ? 'semana anterior' : 'previous week')
            : days === 30 ? (isEs ? 'mes anterior' : 'previous month')
              : days === 90 ? (isEs ? 'trimestre anterior' : 'previous quarter')
                : days === 180 ? (isEs ? '6 meses anteriores' : 'previous 6 months')
                  : days === 365 ? (isEs ? 'año anterior' : 'previous year')
                    : (isEs ? `${days} días anteriores` : `previous ${days} days`);
          const tipFor = (cur, prev, pct, fmt, perDay) => {
            const rows = [[isEs ? 'Este periodo' : 'This period', fmt(cur)]];
            if (c && prev != null) {
              rows.push([`Vs ${cmpTxt}`, fmt(prev)]);
              if (typeof pct === 'number') rows.push([isEs ? 'Cambio' : 'Change', `${pct >= 0 ? '▲ +' : '▼ '}${pct}%`]);
            }
            if (perDay) rows.push([isEs ? 'Promedio/día' : 'Avg/day', fmt(cur / days)]);
            return { title: `${d.window.from} → ${d.window.to}`, rows };
          };
          return (
            <>
              <RStat label={isEs ? 'Ventas' : 'Sales'} value={money(d.totals.total)} testid="rest-rev-total"
                delta={c?.total_pct} tip={tipFor(d.totals.total, c?.total, c?.total_pct, money, true)} />
              <RStat label={isEs ? 'Órdenes' : 'Orders'} value={d.totals.orders.toLocaleString()} testid="rest-rev-orders"
                delta={c?.orders_pct} tip={tipFor(d.totals.orders, c?.orders, c?.orders_pct, (n) => Math.round(n).toLocaleString(), true)} />
              <RStat label={isEs ? 'Ticket promedio' : 'Avg ticket'} value={money2(d.totals.avg_ticket)} testid="rest-rev-ticket"
                delta={c?.ticket_pct} tip={tipFor(d.totals.avg_ticket, c?.avg_ticket, c?.ticket_pct, money2, false)} />
            </>
          );
        })()}
      </div>

      {!!d.mix?.length && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="rest-rev-mix">
          {d.mix.map((m) => (
            <span key={m.key} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHANNELS[m.key].dot }} />
              {isEs ? CHANNELS[m.key].es : CHANNELS[m.key].en}
              <b className="tabular-nums text-gray-900">{money(m.total)}</b>
              <span className="text-gray-400 tabular-nums">· {m.share_pct}%</span>
            </span>
          ))}
        </div>
      )}

      <RCard className="p-4 sm:p-6 mt-4" testid="rest-rev-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">
            {d.granularity === 'month' ? (isEs ? 'Ventas por mes' : 'Sales by month') : (isEs ? 'Ventas por día' : 'Sales by day')}
            <span className="ml-1.5 normal-case tracking-normal text-gray-300 font-semibold">· {isEs ? 'pasa el cursor para el detalle' : 'hover for detail'}</span>
          </p>
          {hasKx && (
            <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1" data-testid="rev-chart-views">
              <button onClick={() => setView('kx')} data-testid="rev-view-kx"
                className={`px-3 py-1 rounded-full text-[11.5px] font-semibold transition-colors ${view === 'kx' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {isEs ? 'Con vs sin Kitxens' : 'With vs without Kitxens'}
              </button>
              <button onClick={() => setView('mix')} data-testid="rev-view-mix"
                className={`px-3 py-1 rounded-full text-[11.5px] font-semibold transition-colors ${view === 'mix' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {isEs ? 'Mezcla de canales' : 'Channel mix'}
              </button>
            </div>
          )}
        </div>
        <RevenueBars series={d.series} granularity={d.granularity} isEs={isEs} view={chartView} />
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-semibold" data-testid="rev-chart-legend">
          {chartView === 'mix' ? (
            ['pos', 'online', 'delivery'].map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-gray-500">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHANNELS[k].dot }} /> {isEs ? CHANNELS[k].es : CHANNELS[k].en}
              </span>
            ))
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 text-gray-500"><span className="w-2.5 h-2.5 rounded-sm bg-gray-300" /> {isEs ? 'Sin Kitxens (estimado)' : 'Without Kitxens (estimate)'}</span>
              <span className="inline-flex items-center gap-1.5 text-[var(--rest-accent)]"><span className="w-2.5 h-2.5 rounded-sm bg-[var(--rest-accent)]" /> {isEs ? 'Aporte Kitxens AI' : 'Kitxens AI added'}</span>
            </>
          )}
        </div>
      </RCard>

      <BreakdownSection isEs={isEs} hasKx={hasKx} />

      <MonthlyChart monthly={d.monthly} isEs={isEs} kitxensSince={d.kitxens_since} />
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RCard className="p-6" testid="rest-rev-top-items">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Top productos' : 'Top products'}</p>
          {d.top_items.length ? d.top_items.map((x) => (
            <div key={x.key} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 text-[13px]">
              <span className="text-gray-600 truncate pr-3">{x.key}</span>
              <span className="tabular-nums text-gray-900 font-semibold">{money(x.total)}</span>
            </div>
          )) : <p className="text-[12.5px] text-gray-400">{isEs ? 'Tu reporte no incluye columna de productos — súbelo con detalle de items para activar esto.' : 'Your report has no item column — upload an item-level export to activate this.'}</p>}
        </RCard>
        <RCard className="p-6" testid="rest-rev-categories">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Top categorías' : 'Top categories'}</p>
          {d.top_categories.length ? d.top_categories.map((x) => (
            <div key={x.key} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 text-[13px]">
              <span className="text-gray-600">{x.key}</span>
              <span className="tabular-nums text-gray-900 font-semibold">{money(x.total)}</span>
            </div>
          )) : <p className="text-[12.5px] text-gray-400">{isEs ? 'Sin columna de categorías en tus reportes.' : 'No category column in your reports yet.'}</p>}
        </RCard>
      </div>
    </div>
  );
}
